/**
 * 브라우저 저장소 안전 래퍼.
 *
 * localStorage 접근은 실패할 수 있다 — 사생활 모드, 용량 초과,
 * Tauri WebView 의 저장소 정책, 파일 프로토콜 오리진. 실패해도 화면은 떠야 한다.
 * 그래서 전부 삼키고 기본값으로 돌아간다.
 *
 * 동기 API 인 이유: 테마처럼 **첫 페인트 전에** 읽어야 하는 값이 있다.
 * 비동기 platform.files 포트로 읽으면 잘못된 테마가 한 프레임 번쩍인다.
 *
 * 🔴 **자격증명은 `session` 영역에 둔다.** 장비 PC 는 공용 단말이라 브라우저를 닫아도 남는
 *    자격이 사고다. 영속이 필요한 값(테마 · 언어)만 `local` 을 쓴다.
 */

/** `local` = 영속(테마·언어) · `session` = 탭이 닫히면 사라짐(자격증명) */
export type StorageArea = 'local' | 'session'

function store(area: StorageArea): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return area === 'session' ? window.sessionStorage : window.localStorage
  } catch {
    return null
  }
}

export const safeStorage = {
  read(key: string, area: StorageArea = 'local'): string | null {
    try {
      return store(area)?.getItem(key) ?? null
    } catch {
      return null
    }
  },

  write(key: string, value: string, area: StorageArea = 'local'): void {
    try {
      store(area)?.setItem(key, value)
    } catch {
      /* 저장 실패는 무시한다 — 이번 세션에서만 적용되고 끝난다 */
    }
  },

  remove(key: string, area: StorageArea = 'local'): void {
    try {
      store(area)?.removeItem(key)
    } catch {
      /* 위와 같다 */
    }
  },

  /** JSON 한 덩어리. 깨진 값은 `null` 로 돌린다 — 저장소가 오염돼도 화면은 떠야 한다 */
  readJson<T>(key: string, area: StorageArea = 'local'): T | null {
    const raw = this.read(key, area)
    if (raw === null) return null
    try {
      return JSON.parse(raw) as T
    } catch {
      return null
    }
  },

  writeJson(key: string, value: unknown, area: StorageArea = 'local'): void {
    try {
      this.write(key, JSON.stringify(value), area)
    } catch {
      /* 순환 참조 등 직렬화 실패도 삼킨다 */
    }
  },
}
